<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import ConversationApi from 'dashboard/api/inbox/conversation';
import QaDashboardApi from 'dashboard/api/qaDashboard';

const { t, locale } = useI18n();
const showAlert = useAlert;
const store = useStore();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const isTemplateLoading = ref(false);
const isTemplateSaving = ref(false);
const selectedAgentId = ref(null);
const selectedTemplateCloseType = ref('complete');
const TEMPLATE_LOCALES = ['th', 'en'];
const parseSupportedLocale = rawLocale => {
  const localeText = String(rawLocale || '')
    .trim()
    .toLowerCase()
    .replace('-', '_');
  if (localeText.startsWith('th')) return 'th';
  if (localeText.startsWith('en')) return 'en';
  return null;
};
const normalizeTemplateLocale = rawLocale =>
  parseSupportedLocale(rawLocale) || 'th';
const normalizeTemplateLocaleKey = rawLocale =>
  String(rawLocale || '')
    .trim()
    .toLowerCase()
    .replace('-', '_') || 'default';
const detectTemplateLocaleByText = templateText => {
  const normalizedText = String(templateText || '').trim();
  if (!normalizedText) return null;
  return /[\u0E00-\u0E7F]/.test(normalizedText) ? 'th' : 'en';
};
const selectedTemplateLocale = ref(normalizeTemplateLocale(locale.value));
const closeTypeTemplateInput = ref('');
const closeTypeTemplates = ref({});
const rows = ref([]);
const remarkByConversation = ref({});
const feedbackByConversation = ref({});

const normalize = text => String(text || '').trim();

const agents = computed(() => store.getters['agents/getAgents'] || []);
const selectedAgent = computed(() =>
  agents.value.find(agent => Number(agent.id) === Number(selectedAgentId.value))
);

const closeTypeLabelMap = {
  complete: 'QA_DASHBOARD.CLOSE_TYPES.COMPLETE',
  no_response: 'QA_DASHBOARD.CLOSE_TYPES.NO_RESPONSE',
  promo_complaint: 'QA_DASHBOARD.CLOSE_TYPES.PROMO_COMPLAINT',
  contact_other_party: 'QA_DASHBOARD.CLOSE_TYPES.CONTACT_OTHER_PARTY',
};

const getCloseTypeLabel = closeType => {
  const key = closeTypeLabelMap[String(closeType || '').trim()];
  return key ? t(key) : t('QA_DASHBOARD.CLOSE_TYPES.UNKNOWN');
};

const closeTypeOptions = computed(() =>
  Object.keys(closeTypeLabelMap).map(closeType => ({
    key: closeType,
    label: getCloseTypeLabel(closeType),
  }))
);

const templateLocaleOptions = computed(() => [
  {
    key: 'th',
    label: t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_LANGUAGE_TH'),
  },
  {
    key: 'en',
    label: t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_LANGUAGE_EN'),
  },
]);

const selectedTemplateLocaleLabel = computed(
  () =>
    templateLocaleOptions.value.find(
      option => option.key === selectedTemplateLocale.value
    )?.label || selectedTemplateLocale.value.toUpperCase()
);

const resolveTemplateValueForEditor = (localeTemplates, templateLocale) => {
  if (!localeTemplates || typeof localeTemplates !== 'object') return '';

  const localeKey = normalizeTemplateLocale(templateLocale);
  const localizedTemplate = String(localeTemplates[localeKey] || '');
  if (normalize(localizedTemplate)) return localizedTemplate;

  const hasAnyLocalizedTemplate = TEMPLATE_LOCALES.some(localeOption =>
    normalize(localeTemplates[localeOption])
  );
  const defaultTemplate = String(localeTemplates.default || '');
  const inferredDefaultLocale = detectTemplateLocaleByText(defaultTemplate);
  if (
    normalize(defaultTemplate) &&
    (!hasAnyLocalizedTemplate || inferredDefaultLocale === localeKey)
  ) {
    return defaultTemplate;
  }
  return '';
};

const getLocaleTemplateValue = (closeType, templateLocale) => {
  const closeTypeKey = String(closeType || '');
  const localeTemplates = closeTypeTemplates.value[closeTypeKey];
  if (
    !closeTypeKey ||
    !localeTemplates ||
    typeof localeTemplates !== 'object'
  ) {
    return '';
  }
  return resolveTemplateValueForEditor(localeTemplates, templateLocale);
};

const syncTemplateInput = () => {
  closeTypeTemplateInput.value = getLocaleTemplateValue(
    selectedTemplateCloseType.value,
    selectedTemplateLocale.value
  );
};

const configuredTemplateCount = computed(
  () =>
    Object.values(closeTypeTemplates.value).filter(localeTemplates =>
      normalize(
        resolveTemplateValueForEditor(
          localeTemplates,
          selectedTemplateLocale.value
        )
      )
    ).length
);

const templateOverviewRows = computed(() =>
  closeTypeOptions.value.map(option => ({
    key: option.key,
    label: option.label,
    th: getLocaleTemplateValue(option.key, 'th'),
    en: getLocaleTemplateValue(option.key, 'en'),
  }))
);

watch(
  [
    selectedTemplateCloseType,
    selectedTemplateLocale,
    () => closeTypeTemplates.value,
  ],
  syncTemplateInput,
  { immediate: true, deep: true }
);

const stats = computed(() => {
  const total = rows.value.length;
  const mismatch = rows.value.filter(row => row.isMismatch).length;
  return {
    total,
    mismatch,
    match: total - mismatch,
  };
});

const templateStats = computed(() => ({
  mismatch: rows.value.filter(row => row.templateStatus === 'mismatch').length,
  match: rows.value.filter(row => row.templateStatus === 'match').length,
}));

const ensureAgentsLoaded = async () => {
  if (agents.value.length) return;
  await store.dispatch('agents/get');
};

const loadCloseTypeTemplates = async () => {
  isTemplateLoading.value = true;
  try {
    const response = await QaDashboardApi.getCloseTypeTemplates(
      selectedTemplateLocale.value
    );
    const items = response?.data?.items || [];
    closeTypeTemplates.value = items.reduce((accumulator, item) => {
      const closeType = String(item.close_type || '');
      if (closeType) {
        const rawTemplateTexts =
          item.template_texts && typeof item.template_texts === 'object'
            ? item.template_texts
            : {};
        const normalizedTemplateTexts = Object.entries(rawTemplateTexts).reduce(
          (templateAccumulator, [rawLocale, templateText]) => {
            const localeKey = normalizeTemplateLocaleKey(rawLocale);
            templateAccumulator[localeKey] = String(templateText || '');
            return templateAccumulator;
          },
          {}
        );
        if (normalize(item.template_text)) {
          normalizedTemplateTexts[selectedTemplateLocale.value] = String(
            item.template_text
          );
        }
        accumulator[closeType] = normalizedTemplateTexts;
      }
      return accumulator;
    }, {});
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.ERROR_LOAD_TEMPLATE')
    );
  } finally {
    isTemplateLoading.value = false;
  }
};

const saveCloseTypeTemplate = async () => {
  if (!selectedTemplateCloseType.value) return;

  isTemplateSaving.value = true;
  try {
    await QaDashboardApi.updateCloseTypeTemplate({
      close_type: selectedTemplateCloseType.value,
      locale: selectedTemplateLocale.value,
      template_text: closeTypeTemplateInput.value.toString(),
    });
    await loadCloseTypeTemplates();
    showAlert(t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.SAVE_TEMPLATE_SUCCESS'));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.ERROR_SAVE_TEMPLATE')
    );
  } finally {
    isTemplateSaving.value = false;
  }
};

const predictCloseType = conversation => {
  const text = String(
    conversation.last_non_activity_message?.content ||
      conversation.messages?.[0]?.content ||
      ''
  ).toLowerCase();

  if (!text) return null;
  if (/(promo|promotion|complain|complaint|scam|fraud)/i.test(text)) {
    return 'promo_complaint';
  }
  if (/(contact other party|third party|contact partner)/i.test(text)) {
    return 'contact_other_party';
  }
  if (/(no response|no reply|unreachable|no answer)/i.test(text)) {
    return 'no_response';
  }
  if (/(done|solved|resolved|thank you|thanks|ok)/i.test(text)) {
    return 'complete';
  }
  return null;
};

const fetchClosedConversationsByAgent = async agentId => {
  const perPage = 25;
  const all = [];
  let page = 1;

  const queryData = {
    payload: [
      {
        attribute_key: 'status',
        filter_operator: 'equal_to',
        values: ['resolved'],
        query_operator: null,
      },
    ],
  };

  const isClosedByAgent = conversation => {
    const targetAgentId = Number(agentId || 0);
    if (!targetAgentId) return false;

    const closedById = Number(
      conversation.custom_attributes?.case_closed_by_id ||
        conversation.additional_attributes?.case_closed_by_id ||
        0
    );
    if (closedById) return closedById === targetAgentId;

    const closedByName = normalize(
      conversation.custom_attributes?.case_closed_by_name ||
        conversation.additional_attributes?.case_closed_by_name
    ).toLowerCase();
    const selectedAgentName = normalize(
      selectedAgent.value?.name
    ).toLowerCase();

    return Boolean(
      closedByName && selectedAgentName && closedByName === selectedAgentName
    );
  };

  while (true) {
    const response = await ConversationApi.filter({ page, queryData });
    const payload = response?.data?.payload || [];
    if (!payload.length) break;

    const matchedPayload = payload.filter(isClosedByAgent);
    all.push(...matchedPayload);

    if (payload.length < perPage) break;
    page += 1;
  }

  return all;
};

const getMessageSenderName = message =>
  String(
    message?.sender_name ||
      message?.sender?.name ||
      message?.sender?.available_name ||
      ''
  );

const isOutgoingBySelectedAgent = message => {
  if (!message || Number(message.message_type) !== 1) return false;

  const selectedAgentIdNumber = Number(selectedAgent.value?.id);
  const senderId = Number(message.sender_id || message.sender?.id || 0);
  if (selectedAgentIdNumber && senderId) {
    return selectedAgentIdNumber === senderId;
  }

  return (
    normalize(getMessageSenderName(message)).toLowerCase() ===
    normalize(selectedAgent.value?.name).toLowerCase()
  );
};

const extractAgentReply = conversation => {
  const candidateMessages = [
    conversation.last_non_activity_message,
    ...(conversation.messages || []),
  ].filter(Boolean);

  const agentMessage = candidateMessages.find(isOutgoingBySelectedAgent);
  if (agentMessage) return String(agentMessage.content || '');

  const fallbackOutgoingMessage = candidateMessages.find(
    message => Number(message.message_type) === 1
  );
  return String(fallbackOutgoingMessage?.content || '');
};

const resolveConversationTemplateLocale = conversation => {
  const localeCandidates = [
    conversation.custom_attributes?.language,
    conversation.custom_attributes?.detected_language,
    conversation.custom_attributes?.locale,
    conversation.additional_attributes?.language,
    conversation.additional_attributes?.detected_language,
    conversation.additional_attributes?.locale,
    conversation.additional_attributes?.browser_language,
  ];

  const attributeLocale = localeCandidates
    .map(parseSupportedLocale)
    .find(Boolean);
  if (attributeLocale) return attributeLocale;

  const customerText = String(
    conversation.last_non_activity_message?.content ||
      conversation.messages?.[0]?.content ||
      ''
  );
  if (normalize(customerText)) {
    return /[\u0E00-\u0E7F]/.test(customerText) ? 'th' : 'en';
  }

  return normalizeTemplateLocale(selectedTemplateLocale.value);
};

const resolveTemplateTextByLocale = (closeType, preferredLocale) => {
  const localeTemplates = closeTypeTemplates.value[String(closeType)] || {};
  if (!localeTemplates || typeof localeTemplates !== 'object') return '';

  const requestedLocale = parseSupportedLocale(preferredLocale);
  const requestedTemplate = requestedLocale
    ? String(localeTemplates[requestedLocale] || '')
    : '';
  if (normalize(requestedTemplate)) {
    return requestedTemplate;
  }

  const defaultTemplate = String(localeTemplates.default || '');
  const inferredDefaultLocale = detectTemplateLocaleByText(defaultTemplate);
  if (
    normalize(defaultTemplate) &&
    (!requestedLocale || inferredDefaultLocale === requestedLocale)
  ) {
    return defaultTemplate;
  }

  const fallbackLocale =
    requestedLocale === 'th' ? 'en' : requestedLocale === 'en' ? 'th' : null;
  const fallbackTemplate = fallbackLocale
    ? String(localeTemplates[fallbackLocale] || '')
    : '';

  return (
    (normalize(fallbackTemplate) && fallbackTemplate) ||
    localeTemplates.th ||
    localeTemplates.en ||
    defaultTemplate ||
    ''
  ).toString();
};

const resolveExpectedTemplate = (closeType, preferredLocale) => {
  const rawTemplate = resolveTemplateTextByLocale(closeType, preferredLocale);
  if (!normalize(rawTemplate)) return '';
  const agentName = selectedAgent.value?.name || '';
  return rawTemplate.toString().replaceAll('{agent_name}', agentName);
};

const getTemplateStatus = ({ closeType, agentReply, templateLocale }) => {
  const expectedTemplate = resolveExpectedTemplate(closeType, templateLocale);
  if (!normalize(expectedTemplate)) return 'not_configured';
  if (!normalize(agentReply)) return 'no_agent_reply';

  return normalize(agentReply) === normalize(expectedTemplate)
    ? 'match'
    : 'mismatch';
};

const getTemplateStatusLabel = status => {
  if (status === 'match') {
    return t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_MATCH');
  }
  if (status === 'mismatch') {
    return t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_MISMATCH');
  }
  if (status === 'no_agent_reply') {
    return t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_NO_AGENT_REPLY');
  }
  return t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_NOT_CONFIGURED');
};

const getTemplateStatusClass = status => {
  if (status === 'match') return 'text-n-teal-11';
  if (status === 'mismatch') return 'text-n-ruby-11';
  return 'text-n-slate-11';
};

const runAudit = async () => {
  if (!selectedAgent.value) return;

  isLoading.value = true;
  rows.value = [];
  remarkByConversation.value = {};
  feedbackByConversation.value = {};
  try {
    const conversations = await fetchClosedConversationsByAgent(
      selectedAgent.value.id
    );
    rows.value = conversations.map(conversation => {
      const actual = conversation.custom_attributes?.case_close_type || null;
      const predicted = predictCloseType(conversation);
      const isMismatch = Boolean(actual && predicted && actual !== predicted);
      const agentReply = extractAgentReply(conversation);
      const conversationLocale =
        resolveConversationTemplateLocale(conversation);
      const expectedTemplate = resolveExpectedTemplate(
        actual,
        conversationLocale
      );
      const templateStatus = getTemplateStatus({
        closeType: actual,
        agentReply,
        templateLocale: conversationLocale,
      });

      return {
        id: conversation.id,
        actual,
        predicted,
        isMismatch,
        expectedTemplate: normalize(expectedTemplate) ? expectedTemplate : '-',
        agentReply: normalize(agentReply) ? agentReply : '-',
        templateStatus,
        conversationRoute: accountScopedRoute('inbox_conversation', {
          conversation_id: conversation.id,
        }),
      };
    });
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.ERROR_RUN_AUDIT')
    );
  } finally {
    isLoading.value = false;
  }
};

const setFeedback = (conversationId, feedback) => {
  feedbackByConversation.value = {
    ...feedbackByConversation.value,
    [conversationId]: feedback,
  };
};

onMounted(async () => {
  await Promise.all([ensureAgentsLoaded(), loadCloseTypeTemplates()]);
});
</script>

<template>
  <div class="qcta-page">
    <div class="qcta-shell">
      <div class="qcta-header">
        <div class="qcta-header-left">
          <button
            class="qcta-side-toggle"
            type="button"
            aria-label="Toggle side navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 4v16M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
              />
            </svg>
          </button>
          <h1 class="qcta-title">
            {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TITLE') }}
          </h1>
        </div>
        <button class="qcta-save-btn" type="button" disabled>Save view</button>
      </div>

      <div class="qcta-content">
        <section class="qcta-card">
          <div class="qcta-card-header">
            <div class="qcta-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="qcta-card-title">
                {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TITLE') }}
              </h2>
            </div>
          </div>
          <div class="qcta-section">
            <p class="qcta-desc">
              {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.DESCRIPTION') }}
            </p>
          </div>
        </section>

        <section class="qcta-card">
          <div class="qcta-card-header">
            <div class="qcta-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6v12m6-6H6"
                />
              </svg>
              <h2 class="qcta-card-title">
                {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_SECTION_TITLE') }}
              </h2>
            </div>

            <div class="qcta-actions">
              <button
                type="button"
                class="qcta-secondary-btn"
                :disabled="isTemplateSaving || isTemplateLoading"
                @click="saveCloseTypeTemplate"
              >
                {{
                  isTemplateSaving
                    ? t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.SAVING_TEMPLATE')
                    : t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.SAVE_TEMPLATE')
                }}
              </button>
            </div>
          </div>

          <div class="qcta-section">
            <div class="qcta-form-grid qcta-form-grid--template">
              <div class="qcta-field">
                <label class="qcta-label">
                  {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_CLOSE_TYPE') }}
                </label>
                <select
                  v-model="selectedTemplateCloseType"
                  class="qcta-input"
                  :disabled="isTemplateSaving || isTemplateLoading"
                >
                  <option
                    v-for="option in closeTypeOptions"
                    :key="option.key"
                    :value="option.key"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div class="qcta-field">
                <label class="qcta-label">
                  {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_LANGUAGE') }}
                </label>
                <select
                  v-model="selectedTemplateLocale"
                  class="qcta-input"
                  :disabled="isTemplateSaving || isTemplateLoading"
                >
                  <option
                    v-for="option in templateLocaleOptions"
                    :key="option.key"
                    :value="option.key"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div class="qcta-field qcta-field--span-2">
                <label class="qcta-label">
                  {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_TEXT') }}
                </label>
                <textarea
                  v-model="closeTypeTemplateInput"
                  rows="3"
                  class="qcta-textarea"
                  :placeholder="
                    t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_TEXT_PLACEHOLDER')
                  "
                  :disabled="isTemplateSaving || isTemplateLoading"
                />
              </div>
            </div>

            <div class="qcta-form-footer">
              <p class="qcta-hint">
                {{
                  t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_HINT', {
                    count: configuredTemplateCount,
                    language: selectedTemplateLocaleLabel,
                  })
                }}
              </p>
            </div>
          </div>

          <div class="qcta-section qcta-section--flush">
            <div class="qcta-subcard">
              <div class="qcta-subcard-header">
                <div>
                  <div class="qcta-subcard-title">
                    {{
                      t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_TITLE')
                    }}
                  </div>
                  <div class="qcta-subcard-desc">
                    {{
                      t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_HINT')
                    }}
                  </div>
                </div>
              </div>

              <div class="qcta-table-wrap">
                <table class="qcta-table">
                  <thead>
                    <tr>
                      <th class="qcta-th">
                        {{
                          t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_CLOSE_TYPE')
                        }}
                      </th>
                      <th class="qcta-th">
                        {{
                          t(
                            'QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_TH'
                          )
                        }}
                      </th>
                      <th class="qcta-th">
                        {{
                          t(
                            'QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_EN'
                          )
                        }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="isTemplateLoading">
                      <td class="qcta-td qcta-td--empty" colspan="3">
                        <div class="qcta-loading-row">
                          <div class="qcta-spinner" />
                          <span>{{
                            t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.LOADING_TEMPLATES')
                          }}</span>
                        </div>
                      </td>
                    </tr>

                    <tr v-for="row in templateOverviewRows" :key="row.key">
                      <td class="qcta-td qcta-td--strong">{{ row.label }}</td>
                      <td class="qcta-td qcta-td--pre">
                        {{
                          row.th ||
                          t(
                            'QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_EMPTY'
                          )
                        }}
                      </td>
                      <td class="qcta-td qcta-td--pre">
                        {{
                          row.en ||
                          t(
                            'QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_EMPTY'
                          )
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <section class="qcta-card">
          <div class="qcta-card-header">
            <div class="qcta-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 5v14l11-7z"
                />
              </svg>
              <h2 class="qcta-card-title">
                {{ t('QA_DASHBOARD.FORM.RUN_AUDIT') }}
              </h2>
            </div>
            <div class="qcta-actions">
              <button
                type="button"
                class="qcta-primary-btn"
                :disabled="isLoading || !selectedAgent"
                @click="runAudit"
              >
                {{
                  isLoading
                    ? t('QA_DASHBOARD.FORM.RUNNING')
                    : t('QA_DASHBOARD.FORM.RUN_AUDIT')
                }}
              </button>
            </div>
          </div>

          <div class="qcta-section">
            <div class="qcta-form-grid qcta-form-grid--audit">
              <div class="qcta-field qcta-field--span-2">
                <label class="qcta-label">{{
                  t('QA_DASHBOARD.FORM.AGENT')
                }}</label>
                <select v-model="selectedAgentId" class="qcta-input">
                  <option :value="null">
                    {{ t('QA_DASHBOARD.FORM.SELECT_AGENT') }}
                  </option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
              </div>
            </div>
            <p class="qcta-hint">
              {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.HINT') }}
            </p>
          </div>
        </section>

        <div class="qcta-metrics">
          <div class="qcta-metric">
            <div class="qcta-metric-label">
              {{ t('QA_DASHBOARD.STATS.TOTAL') }}
            </div>
            <div class="qcta-metric-value">{{ stats.total }}</div>
          </div>
          <div class="qcta-metric">
            <div class="qcta-metric-label">
              {{ t('QA_DASHBOARD.STATS.MATCH') }}
            </div>
            <div class="qcta-metric-value qcta-metric-value--good">
              {{ stats.match }}
            </div>
          </div>
          <div class="qcta-metric">
            <div class="qcta-metric-label">
              {{ t('QA_DASHBOARD.STATS.MISMATCH') }}
            </div>
            <div class="qcta-metric-value qcta-metric-value--bad">
              {{ stats.mismatch }}
            </div>
          </div>
          <div class="qcta-metric">
            <div class="qcta-metric-label">
              {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_MISMATCH_COUNT') }}
            </div>
            <div class="qcta-metric-value qcta-metric-value--bad">
              {{ templateStats.mismatch }}
              <span class="qcta-metric-subtle">
                / {{ templateStats.match + templateStats.mismatch }}
              </span>
            </div>
          </div>
        </div>

        <section class="qcta-card">
          <div class="qcta-card-header">
            <div class="qcta-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7h13M8 12h13M8 17h13M3 7h.01M3 12h.01M3 17h.01"
                />
              </svg>
              <h2 class="qcta-card-title">Results</h2>
            </div>
          </div>

          <div class="qcta-table-wrap">
            <table class="qcta-table qcta-table--results">
              <thead>
                <tr>
                  <th class="qcta-th">
                    {{ t('QA_DASHBOARD.TABLE.CONVERSATION') }}
                  </th>
                  <th class="qcta-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.ACTUAL') }}
                  </th>
                  <th class="qcta-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.PREDICTED') }}
                  </th>
                  <th class="qcta-th">{{ t('QA_DASHBOARD.TABLE.STATUS') }}</th>
                  <th class="qcta-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.EXPECTED_TEMPLATE') }}
                  </th>
                  <th class="qcta-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.AGENT_REPLY') }}
                  </th>
                  <th class="qcta-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_STATUS') }}
                  </th>
                  <th class="qcta-th">{{ t('QA_DASHBOARD.TABLE.REMARK') }}</th>
                  <th class="qcta-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.FEEDBACK') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading">
                  <td class="qcta-td qcta-td--empty" colspan="9">
                    <div class="qcta-loading-row">
                      <div class="qcta-spinner" />
                      <span>{{ t('QA_DASHBOARD.FORM.RUNNING') }}</span>
                    </div>
                  </td>
                </tr>

                <tr v-for="row in rows" :key="row.id">
                  <td class="qcta-td">
                    <RouterLink class="qcta-link" :to="row.conversationRoute">
                      #{{ row.id }}
                    </RouterLink>
                  </td>
                  <td class="qcta-td">{{ getCloseTypeLabel(row.actual) }}</td>
                  <td class="qcta-td">
                    {{ getCloseTypeLabel(row.predicted) }}
                  </td>
                  <td class="qcta-td">
                    <span
                      class="qcta-status"
                      :class="
                        row.isMismatch ? 'qcta-status--bad' : 'qcta-status--ok'
                      "
                    >
                      {{
                        row.isMismatch
                          ? t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.MISMATCH')
                          : t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.MATCH')
                      }}
                    </span>
                  </td>
                  <td class="qcta-td qcta-td--pre">
                    {{ row.expectedTemplate }}
                  </td>
                  <td class="qcta-td qcta-td--pre">{{ row.agentReply }}</td>
                  <td class="qcta-td">
                    <span
                      class="qcta-template-status"
                      :class="`qcta-template-status--${row.templateStatus}`"
                    >
                      {{ getTemplateStatusLabel(row.templateStatus) }}
                    </span>
                  </td>
                  <td class="qcta-td">
                    <input
                      v-model="remarkByConversation[row.id]"
                      class="qcta-input qcta-input--sm"
                      :placeholder="
                        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.REMARK_PLACEHOLDER')
                      "
                    />
                  </td>
                  <td class="qcta-td">
                    <div class="qcta-feedback">
                      <button
                        type="button"
                        class="qcta-chip"
                        :class="
                          feedbackByConversation[row.id] === 'ai_correct'
                            ? 'qcta-chip--active qcta-chip--good'
                            : ''
                        "
                        @click="setFeedback(row.id, 'ai_correct')"
                      >
                        {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.AI_CORRECT') }}
                      </button>
                      <button
                        type="button"
                        class="qcta-chip"
                        :class="
                          feedbackByConversation[row.id] === 'ai_wrong'
                            ? 'qcta-chip--active qcta-chip--bad'
                            : ''
                        "
                        @click="setFeedback(row.id, 'ai_wrong')"
                      >
                        {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.AI_WRONG') }}
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="!rows.length && !isLoading">
                  <td class="qcta-td qcta-td--empty" colspan="9">
                    {{ t('QA_DASHBOARD.TABLE.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* === Close-Type Audit — always "white report" (hardcoded colors, no CSS vars) === */
.qcta-page {
  background: #ffffff;
  color: #111827;
}

.qcta-shell {
  max-width: 1180px;
  margin: 0 auto;
  padding: 16px 0 40px;
}

.qcta-header {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
}

.qcta-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.qcta-side-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
}

.qcta-side-toggle:hover {
  background: #f9fafb;
}

.qcta-title {
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qcta-save-btn {
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #9ca3af;
  font-weight: 600;
  font-size: 13px;
  cursor: not-allowed;
}

.qcta-content {
  display: grid;
  gap: 16px;
}

.qcta-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.qcta-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.qcta-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: #111827;
}

.qcta-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.qcta-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.qcta-section {
  padding: 16px 20px 20px;
}

.qcta-section--flush {
  padding: 0;
}

.qcta-desc {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
}

.qcta-form-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 12px;
}

.qcta-form-grid--template .qcta-field:nth-child(1),
.qcta-form-grid--template .qcta-field:nth-child(2) {
  grid-column: span 3;
}

.qcta-form-grid--template .qcta-field--span-2 {
  grid-column: span 6;
}

.qcta-form-grid--audit .qcta-field--span-2 {
  grid-column: span 6;
}

.qcta-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.qcta-label {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}

.qcta-input,
.qcta-textarea {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  outline: none;
}

.qcta-input:focus,
.qcta-textarea:focus {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.qcta-textarea {
  resize: vertical;
}

.qcta-input:disabled,
.qcta-textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qcta-input--sm {
  padding: 8px 10px;
}

.qcta-form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding-bottom: 16px;
}

.qcta-hint {
  margin: 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;
}

.qcta-primary-btn,
.qcta-secondary-btn {
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
}

.qcta-primary-btn {
  background: #111827;
  color: #ffffff;
}

.qcta-primary-btn:hover {
  opacity: 0.9;
}

.qcta-secondary-btn {
  background: #ffffff;
  color: #111827;
  border-color: #d1d5db;
}

.qcta-secondary-btn:hover {
  background: #f9fafb;
}

.qcta-primary-btn:disabled,
.qcta-secondary-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.qcta-subcard {
  border-top: 1px solid #e5e7eb;
}

.qcta-subcard-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.qcta-subcard-title {
  font-weight: 700;
  color: #111827;
  font-size: 13px;
}

.qcta-subcard-desc {
  color: #6b7280;
  font-size: 12px;
  margin-top: 2px;
}

.qcta-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.qcta-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}

.qcta-metric-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
}

.qcta-metric-value {
  margin-top: 8px;
  font-size: 24px;
  line-height: 28px;
  font-weight: 800;
  color: #111827;
}

.qcta-metric-value--good {
  color: #16a34a;
}

.qcta-metric-value--bad {
  color: #dc2626;
}

.qcta-metric-subtle {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  margin-left: 4px;
}

.qcta-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.qcta-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.qcta-th {
  text-align: left;
  padding: 10px 12px;
  color: #6b7280;
  font-weight: 800;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  white-space: nowrap;
}

.qcta-td {
  padding: 10px 12px;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
  vertical-align: top;
  background: #ffffff;
}

.qcta-td--strong {
  font-weight: 700;
}

.qcta-td--pre {
  white-space: pre-wrap;
  word-break: break-word;
  min-width: 200px;
  max-width: 260px;
}

.qcta-td--empty {
  color: #6b7280;
  text-align: center;
  padding: 24px 12px;
}

.qcta-link {
  color: #111827;
  font-weight: 800;
  text-decoration: underline;
  text-underline-position: from-font;
}

.qcta-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
}

.qcta-status--ok {
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
}

.qcta-status--bad {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.qcta-template-status {
  font-weight: 700;
  color: #374151;
}

.qcta-template-status--match {
  color: #16a34a;
}

.qcta-template-status--mismatch {
  color: #dc2626;
}

.qcta-template-status--no_agent_reply,
.qcta-template-status--not_configured {
  color: #6b7280;
}

.qcta-feedback {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.qcta-chip {
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.qcta-chip:hover {
  background: #f9fafb;
}

.qcta-chip--active.qcta-chip--good {
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
  border-color: rgba(22, 163, 74, 0.3);
}

.qcta-chip--active.qcta-chip--bad {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
  border-color: rgba(220, 38, 38, 0.3);
}

.qcta-loading-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #6b7280;
  font-weight: 700;
}

.qcta-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid #e5e7eb;
  border-top-color: #6b7280;
  animation: qcta-spin 0.8s linear infinite;
}

@keyframes qcta-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .qcta-shell {
    padding: 12px 0 32px;
  }

  .qcta-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .qcta-form-grid--template .qcta-field:nth-child(1),
  .qcta-form-grid--template .qcta-field:nth-child(2),
  .qcta-form-grid--template .qcta-field--span-2,
  .qcta-form-grid--audit .qcta-field--span-2 {
    grid-column: span 12;
  }
}

@media (max-width: 640px) {
  .qcta-save-btn {
    display: none;
  }
}
</style>
